import Service, { service } from '@ember/service';
import type CableService from './cable.ts';
import type { Appearance } from './appearance.ts';

// Simple format: document_id maps to array of Appearance objects
export type MultiDocumentAppearanceMap = {
  [documentId: string]: Appearance[];
};

/**
 * @deprecated Legacy type - use Appearance[] directly
 * Kept for backwards compatibility during transition
 */
export type MultiDocumentPersonAppearanceMap = {
  [key: string]: Appearance;
};

export default class MultiDocumentAppearanceService extends Service {
  @service declare cable: CableService;

  // Track multiple subscriptions by type
  private subscriptionsByType = new Map<string, object>();

  /**
   * @deprecated Cable connection is now managed centrally.
   * Apps should call cable.configure() at startup instead.
   */
  connect(apiHost: string, apiKey: string, token: string) {
    this.cable.connect(apiHost, apiKey, token);
  }

  subscribe(
    type: string,
    documentIds: string[],
    onUpdate: (msg: MultiDocumentAppearanceMap) => void
  ) {
    // Unsubscribe from existing subscription for this type first
    if (this.subscriptionsByType.has(type)) {
      this.cable.unsubscribe(this.subscriptionsByType.get(type)!);
    }

    const channelParams = {
      channel: 'MultiDocumentAppearanceChannel',
      type,
      document_ids: documentIds,
    };

    this.subscriptionsByType.set(type, channelParams);

    this.cable.subscribe(channelParams, {
      received: (msg) => {
        onUpdate(msg as MultiDocumentAppearanceMap);
      },
    });
  }

  unsubscribe(type: string, _documentIds: string[]) {
    const channelParams = this.subscriptionsByType.get(type);
    if (channelParams) {
      this.cable.unsubscribe(channelParams);
      this.subscriptionsByType.delete(type);
    }
  }

  /**
   * Unsubscribe from all active subscriptions
   */
  unsubscribeAll() {
    for (const [type, channelParams] of this.subscriptionsByType) {
      this.cable.unsubscribe(channelParams);
      this.subscriptionsByType.delete(type);
    }
  }
}
